/**
 * @file Account identity selfie quality confirmation component.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import IdentityAvatar from '../../../components/global/identity/IdentityAvatar';
import CustomControl from '../../../components/global/CustomControl';
import Button from '../../../components/global/Button';
import { AVATAR_SIZE_72 } from '../../../constants/avatar-sizes';
import { PHOTO_VIEW_MODAL } from '../../../constants/modals';
import ZoomInIcon from '../../../components/icons/ZoomInIcon';
import PhotoViewModal from '../../../components/global/modal/PhotoViewModal';
import withVirtualPageview from '../../../components/global/hoc/withVirtualPageview';

class AccountIdentitySelfieConfirmComponent extends Component {
  static propTypes = {
    photoUrl: PropTypes.string,
    onSelfieConfirm: PropTypes.func.isRequired,
    onSelfieReject: PropTypes.func.isRequired,
    checks: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ),
    skipManualCheck: PropTypes.bool,
    openModal: PropTypes.func.isRequired,
    account: PropTypes.shape({
      id: PropTypes.string,
    }),
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
    buttonsDisabled: PropTypes.bool,
  };

  static defaultProps = {
    skipManualCheck: true,
    checks: [
      {
        id: 'recency',
        label: 'Recent pic (taken last 30 days)',
      },
      {
        id: 'sunglasses',
        label: 'No sunglasses or hats',
      },
      {
        id: 'light',
        label: 'Lots of light',
      },
      {
        id: 'proximity',
        label: 'Close to camera',
      },
      {
        id: 'filters',
        label: 'No filters or effects',
      },
    ],
  };

  constructor(props) {
    super(props);

    const { checks, skipManualCheck } = this.props;

    this.state = {
      selfieConfirmed: skipManualCheck,
      checks: checks.reduce(
        (state, check) => ({
          ...state,
          // Start with all checkboxes pre-selected
          [check.id]: skipManualCheck,
        }),
        {},
      ),
    };
  }

  onInputChange = event => {
    const { checked, name } = event.target;

    this.setState(
      state => ({
        ...state,
        checks: {
          ...state.checks,
          [name]: checked,
        },
      }),
      () => {
        // If all checks are confirmed then allow
        // the user to continue
        this.setState(() => ({
          selfieConfirmed: this.allChecksConfirmed(),
        }));
      },
    );
  };

  allChecksConfirmed = () => {
    const { checks } = this.state;

    return Object.keys(checks).every(key => checks[key]);
  };

  render() {
    const { selfieConfirmed } = this.state;
    const {
      photoUrl,
      onSelfieConfirm,
      onSelfieReject,
      checks,
      skipManualCheck,
      openModal,
      buttonsDisabled = false,
    } = this.props;

    return (
      <Fragment>
        {photoUrl && (
          <Fragment>
            <div className="text-center mb-2">
              <IdentityAvatar
                uploadedPhoto={{
                  url: photoUrl,
                }}
                size={AVATAR_SIZE_72}
                onClick={() => openModal(PHOTO_VIEW_MODAL)}
              >
                <span className="btn-identity-avatar-zoom">
                  <ZoomInIcon color="#0e84fe" />
                </span>
              </IdentityAvatar>
            </div>

            <PhotoViewModal photoUrl={photoUrl} />
          </Fragment>
        )}

        <div className="text-center mb-3">
          <h3 className="mb-2">
            Let&rsquo;s check to see if this
            <br />
            is a great photo!
          </h3>
          <p className="m-0">
            {skipManualCheck
              ? 'Please confirm the following:'
              : 'Please tap on the checkboxes to confirm:'}
          </p>
        </div>

        <div className="d-flex flex-column mb-2 px-3">
          {checks.map(check => (
            <CustomControl
              key={check.id}
              type="checkbox"
              id={check.id}
              name={check.id}
              label={check.label}
              className={classNames({
                'd-block mb-2 text-bold': true,
                'text-dark': this.state.checks[check.id],
              })}
              size="lg"
              checked={this.state.checks[check.id]}
              onChange={skipManualCheck ? undefined : this.onInputChange}
              readOnly={skipManualCheck}
            />
          ))}
        </div>

        <div className="text-center">
          <Button
            id="confirm-selfie"
            btnBlock
            onClick={onSelfieConfirm}
            className="mb-1"
            disabled={!selfieConfirmed || buttonsDisabled}
          >
            Continue
          </Button>

          <Button
            btnBlock
            btnOutline
            onClick={onSelfieReject}
            disabled={buttonsDisabled}
          >
            Change My Photo
          </Button>
        </div>
      </Fragment>
    );
  }
}

export { AccountIdentitySelfieConfirmComponent };

export default withVirtualPageview({
  path: 'selfie-confirm',
  title: 'Selfie Confirmation',
})(AccountIdentitySelfieConfirmComponent);
